import { Contract } from "@/types/field";
import { FormProvider, useForm } from "react-hook-form";
import { object, number, date, string, InferType } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect } from "react";
import dayjs from "dayjs";
import {
    Button,
    Divider,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
} from "@heroui/react";
import { Organization } from "@/types/organization";
import useRequest from "@/lib/request/useRequest";
import AutoCompleteInput from "../inputs/AutoCompleteInput";
import TextAreaInput from "../inputs/TextAreaInput";
import {
    EnrollmentTier,
    getEnrollmentTierLabel,
    getRetailPrice,
} from "@/lib/utils/enrollment";
import TextInput from "../inputs/TextInput";
import DateInput from "../inputs/DateInput";

const lineItemSchema = object().shape({
    acres: number().nullable(),
    enrollment_tier: number().nullable(),
    retail_price: number().nullable(),
    contracted_price: number().nullable(),
});

const schema = object().shape({
    organization: number().required(),
    contract_date: date().required(),
    basic: lineItemSchema,
    advanced: lineItemSchema,
    standard: lineItemSchema,
    advancedV2: lineItemSchema,
    complete: lineItemSchema,
    notes: string().nullable(),
    termination_date: date().required(),
});
function ContractForm({
    existingContract,
    organizations,
    onClose,
}: {
    existingContract?: Contract;
    organizations: Organization[];
    onClose: (shouldRefetch: boolean) => void;
}) {
    const { useMutateRequest } = useRequest();
    const f = useForm<InferType<typeof schema>>({
        resolver: yupResolver(schema),
        defaultValues: {
            contract_date: dayjs().toDate(),
            basic: {
                acres: 0,
                enrollment_tier: EnrollmentTier.Basic,
                retail_price: getRetailPrice(EnrollmentTier.Basic),
                contracted_price: getRetailPrice(EnrollmentTier.Basic),
            },
            advanced: {
                acres: 0,
                enrollment_tier: EnrollmentTier.Advanced,
                retail_price: getRetailPrice(EnrollmentTier.Advanced),
                contracted_price: getRetailPrice(EnrollmentTier.Advanced),
            },
            standard: {
                acres: 0,
                enrollment_tier: EnrollmentTier.Standard,
                retail_price: getRetailPrice(EnrollmentTier.Standard),
                contracted_price: getRetailPrice(EnrollmentTier.Standard),
            },
            advancedV2: {
                acres: 0,
                enrollment_tier: EnrollmentTier.AdvancedV2,
                retail_price: getRetailPrice(EnrollmentTier.AdvancedV2),
                contracted_price: getRetailPrice(EnrollmentTier.AdvancedV2),
            },
            complete: {
                acres: 0,
                enrollment_tier: EnrollmentTier.Complete,
                retail_price: getRetailPrice(EnrollmentTier.Complete),
                contracted_price: getRetailPrice(EnrollmentTier.Complete),
            },
            termination_date: dayjs().add(1, "year").toDate(),
        },
    });

    useEffect(() => {
        if (existingContract) {
            const basic = existingContract.line_items.find(
                (item) => item.enrollment_tier === EnrollmentTier.Basic,
            );
            const advanced = existingContract.line_items.find(
                (item) => item.enrollment_tier === EnrollmentTier.Advanced,
            );
            const standard = existingContract.line_items.find(
                (item) => item.enrollment_tier === EnrollmentTier.Standard,
            );
            const advancedV2 = existingContract.line_items.find(
                (item) => item.enrollment_tier === EnrollmentTier.AdvancedV2,
            );
            const complete = existingContract.line_items.find(
                (item) => item.enrollment_tier === EnrollmentTier.Complete,
            );
            f.reset({
                // @ts-expect-error inputs
                organization: existingContract.organization.toString(),
                contract_date: dayjs(existingContract.contract_date).toDate(),
                basic: {
                    acres: basic?.acres ?? 0,
                    enrollment_tier:
                        basic?.enrollment_tier ?? EnrollmentTier.Basic,
                    retail_price: Number(
                        basic?.retail_price ??
                            getRetailPrice(EnrollmentTier.Basic),
                    ),
                    contracted_price: Number(basic?.contracted_price ?? 0),
                },
                advanced: {
                    acres: advanced?.acres ?? 0,
                    enrollment_tier:
                        advanced?.enrollment_tier ?? EnrollmentTier.Advanced,
                    retail_price: Number(
                        advanced?.retail_price ??
                            getRetailPrice(EnrollmentTier.Advanced),
                    ),
                    contracted_price: Number(advanced?.contracted_price ?? 0),
                },
                standard: {
                    acres: standard?.acres ?? 0,
                    enrollment_tier:
                        standard?.enrollment_tier ?? EnrollmentTier.Standard,
                    retail_price: Number(
                        standard?.retail_price ??
                            getRetailPrice(EnrollmentTier.Standard),
                    ),
                    contracted_price: Number(standard?.contracted_price ?? 0),
                },
                advancedV2: {
                    acres: advancedV2?.acres ?? 0,
                    enrollment_tier:
                        advancedV2?.enrollment_tier ??
                        EnrollmentTier.AdvancedV2,
                    retail_price: Number(
                        advancedV2?.retail_price ??
                            getRetailPrice(EnrollmentTier.AdvancedV2),
                    ),
                    contracted_price: Number(advancedV2?.contracted_price ?? 0),
                },
                complete: {
                    acres: complete?.acres ?? 0,
                    enrollment_tier:
                        complete?.enrollment_tier ?? EnrollmentTier.Complete,
                    retail_price: Number(
                        complete?.retail_price ??
                            getRetailPrice(EnrollmentTier.Complete),
                    ),
                    contracted_price: Number(complete?.contracted_price ?? 0),
                },
                notes: existingContract.notes,
            });
        }
    }, [existingContract, f]);

    const contractMutation = useMutateRequest({
        errorMessage: "Failed to save contract",
        successMessage: "Contract saved successfully",
    });

    const onSubmit = (data: InferType<typeof schema>) => {
        const payload = {
            ...data,
            line_items: [
                data.basic,
                data.advanced,
                data.advancedV2,
                data.complete,
                data.standard,
            ].filter((item) => !!item.acres && !!item.retail_price),
            contract_date: dayjs(data.contract_date).format("YYYY-MM-DD"),
            termination_date: dayjs(data.termination_date).format("YYYY-MM-DD"),
        };
        const onSuccess = () => {
            onClose(true);
        };
        if (existingContract) {
            contractMutation.mutate(
                {
                    method: "PUT",
                    url: `/contracts/${existingContract.id}/`,
                    data: payload,
                },
                { onSuccess },
            );
        } else {
            contractMutation.mutate(
                {
                    method: "POST",
                    url: "/contracts/",
                    data: payload,
                },
                { onSuccess },
            );
        }
    };

    return (
        <FormProvider {...f}>
            <form>
                <Modal
                    isOpen
                    onClose={() => onClose(false)}
                    size="2xl"
                    isDismissable={false}
                >
                    <ModalContent>
                        <ModalHeader>
                            {existingContract ? "Edit" : "Add New"} Contract
                        </ModalHeader>
                        <ModalBody>
                            <div className="flex flex-col gap-2">
                                <AutoCompleteInput
                                    name="organization"
                                    label="CSP"
                                    labelPlacement="outside"
                                    placeholder="Select Organization"
                                    options={organizations.map((org) => ({
                                        id: org.id.toString(),
                                        description: org.name,
                                    }))}
                                />
                                <DateInput
                                    name="contract_date"
                                    label="Contract Date"
                                    labelPlacement="outside"
                                />
                                <DateInput
                                    name="termination_date"
                                    label="Termination Date"
                                    labelPlacement="outside"
                                />
                            </div>
                            <Divider />
                            <div className="flex flex-col gap-2">
                                <div className="text-lg font-semibold">
                                    Acre Amounts
                                </div>
                                <div className="grid grid-cols-3 gap-2">
                                    {[
                                        [EnrollmentTier.Standard, "standard"],
                                        [
                                            EnrollmentTier.AdvancedV2,
                                            "advancedV2",
                                        ],
                                        [EnrollmentTier.Complete, "complete"],
                                    ].map(([tier, name]) => (
                                        <div
                                            key={name}
                                            className="flex flex-col gap-2"
                                        >
                                            <div>
                                                Tier:{" "}
                                                <span className="font-semibold">
                                                    {getEnrollmentTierLabel(
                                                        tier as number,
                                                    )}
                                                </span>
                                            </div>
                                            <TextInput
                                                name={`${name}.acres`}
                                                label="Acres"
                                                labelPlacement="outside"
                                                placeholder=" "
                                            />
                                            <TextInput
                                                name={`${name}.retail_price`}
                                                label="Retail Price"
                                                labelPlacement="outside"
                                                type="number"
                                                startContent="$"
                                                placeholder=" "
                                                isReadOnly
                                                isDisabled
                                            />
                                            <TextInput
                                                name={`${name}.contracted_price`}
                                                label="Contract Price"
                                                labelPlacement="outside"
                                                type="number"
                                                startContent="$"
                                                placeholder=" "
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <Divider />
                            <TextAreaInput
                                name="notes"
                                label="Notes"
                                labelPlacement="outside"
                            />
                        </ModalBody>
                        <ModalFooter>
                            <Button
                                size="sm"
                                onPress={() => onClose(false)}
                                fullWidth
                                isDisabled={contractMutation.isPending}
                            >
                                Cancel
                            </Button>
                            <Button
                                size="sm"
                                color="primary"
                                fullWidth
                                isLoading={contractMutation.isPending}
                                isDisabled={contractMutation.isPending}
                                onPress={() => {
                                    f.trigger();
                                    f.handleSubmit(onSubmit)();
                                }}
                            >
                                Save
                            </Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>
            </form>
        </FormProvider>
    );
}

export default ContractForm;
